import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography, useTheme } from "@mui/material";
import { IRoadLayoutConfig, IRoadConfigLayoutConfigCallBack, ILuminaireLayoutConfigCallBack, ILuminaireLayoutConfig, CarriageWayEnum } from "types";
import { images, getCarriageWayImage } from "utils/images";
import { RadioCard } from "components/RadioCard";
import { CheckCard } from "components/CheckCard";
import { useRoadLayout } from "./useRoadLayout";
import getStyles from "./styles";

type Props = {
  roadLayoutConfig: IRoadLayoutConfig;
  luminaireLayoutConfig: ILuminaireLayoutConfig;
  onRoadLayoutConfig: IRoadConfigLayoutConfigCallBack;
  onLuminaireLayoutConfig: ILuminaireLayoutConfigCallBack;
};

const RoadLayout: FC<Props> = ({ roadLayoutConfig, luminaireLayoutConfig, onRoadLayoutConfig, onLuminaireLayoutConfig }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { handleBorderSelect, handleCarriagewaySelect } = useRoadLayout(roadLayoutConfig, luminaireLayoutConfig, onRoadLayoutConfig, onLuminaireLayoutConfig);

  const carriagewayOptions = useMemo(
    () => [
      {
        label: t("Single Carriageway"),
        value: CarriageWayEnum.SINGLE,
        img: getCarriageWayImage(roadLayoutConfig, CarriageWayEnum.SINGLE),
      },
      {
        label: t("Double Carriageway"),
        value: CarriageWayEnum.DOUBLE,
        img: getCarriageWayImage(roadLayoutConfig, CarriageWayEnum.DOUBLE),
      },
    ],
    [roadLayoutConfig, t],
  );

  const borderOptions = useMemo(
    () => [
      {
        label: t("Add border area left"),
        name: "left",
        value: "left",
        checked: !!roadLayoutConfig.borderAreaLeft,
        img: images.road.border.left,
      },
      {
        label: t("Add border area right"),
        name: "right",
        value: "right",
        checked: !!roadLayoutConfig.borderAreaRight,
        img: images.road.border.right,
      },
    ],
    [roadLayoutConfig.borderAreaLeft, roadLayoutConfig.borderAreaRight, t],
  );

  return (
    <Stack sx={styles.container}>
      <Typography variant="h2" sx={styles.title}>
        {t("Road Layout")}
      </Typography>

      <RadioCard
        name="carrigeway"
        value={roadLayoutConfig.carriageWay}
        onSelect={handleCarriagewaySelect as (value: string | number) => void}
        options={carriagewayOptions}
        sx={{ mb: "var(--rhythm)" }}
      />

      <Typography variant="h2" sx={styles.title}>
        {t("Border")}
      </Typography>

      <CheckCard
        onSelect={handleBorderSelect}
        options={borderOptions}
      />
    </Stack>
  );
};

export default memo(RoadLayout);
